// 目前登入前的頁面所 call 的 API，都是直接用 axios，沒使用 request 包裝後的
// 原因為 validateStatus 的設定，登入前頁面對於 API error 時的狀態顯示都是一樣的，
// 沒有分 400、500，所以如果登入前頁面要使用 request 包裝的話，必須針對錯誤狀態做分流處理。

import axios from 'axios';
import * as qs from 'qs';
import * as R from 'ramda';
import { getEnvironmentBaseURL, getEnvironmentOnSiteBaseURL } from '@Utils/commonFunction';

import { DEFAULT_EVENT_TIME_ZONE } from '@Constants/eventConstants';

import { store } from './store';

const baseUrl = getEnvironmentBaseURL();
const testBaseUrl = 'http://localhost:3001';
const onsiteBaseUrl = getEnvironmentOnSiteBaseURL();

const validateStatusConfig = {
  validateStatus: (status) => {
    return status >= 200 && status <= 499;
  },
};

// ------------ funevent ---------
const request = axios.create({
  baseURL: baseUrl,
  ...validateStatusConfig,
});

request.interceptors.request.use((config) => {
  const requestConfig = config;
  if (store.getState()?.login?.userAuth) {
    requestConfig.headers.Authorization = `Bearer ${store.getState().login.userAuth}`;
  }

  return requestConfig;
});

request.interceptors.response.use(
  (data) => {
    return Promise.resolve(data);
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ------------ onsite ---------
const onsiteRequest = axios.create({
  baseURL: onsiteBaseUrl,
  ...validateStatusConfig,
});

onsiteRequest.interceptors.request.use((config) => {
  const requestConfig = config;
  if (store.getState()?.login?.userAuth) {
    requestConfig.headers.Authorization = `Bearer ${store.getState().login.userAuth}`;
  }

  return requestConfig;
});

onsiteRequest.interceptors.response.use(
  (data) => {
    return Promise.resolve(data);
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ------------ 系統相關 --------
// 取得目前版號，與是否要維修
export const getCheckVersion = (v) =>
  request({
    method: 'get',
    url: `/api/v3/App/CheckVersion/funevent?v=${v}`,
  });

export const postRegisterAuthenticated = (token) =>
  axios({
    method: 'get',
    url: `${baseUrl}/api/v3/admin/verifyemail/${token}`,
  });

export const postRegister = (
  account,
  password,
  name,
  company,
  cellPhone,
  taxIDNumber,
  industry,
  language = 'default',
) =>
  axios({
    method: 'post',
    url: `${baseUrl}/api/v3/admin/create`,
    data: {
      account,
      password,
      name,
      company,
      cellPhone,
      taxIDNumber,
      industry,
      language,
    },
  });

export const checkRepeatAccount = (account) =>
  axios({
    method: 'post',
    url: `${baseUrl}/api/v3/admin/checkaccount`,
    data: { account },
  });

export const checkLogin = (data) =>
  request({
    method: 'post',
    url: '/api/v6/admin/login',
    data,
  });

// 登出其它裝置
export const postLogoutOtherDevices = () =>
  request({
    method: 'delete',
    url: `/api/v6/admin/logoutothers`,
  });

// 登出
export const postLogout = () =>
  request({
    method: 'delete',
    url: `/api/v6/admin/logout`,
  });

// 檢查 token 是否過期
export const checkTokenExpired = () =>
  request({
    method: 'get',
    url: `/api/v3/admin/checktoken`,
  });

export const reSendRegisterAuthenticated = (account) =>
  axios({
    method: 'post',
    url: `${baseUrl}/api/v3/admin/verifyemail/send`,
    data: { account },
  });

export const sendForgotPasswordEmail = (account) =>
  axios({
    method: 'post',
    url: `${baseUrl}/api/v3/admin/forgotpassword`,
    data: { account },
  });

export const resetNewPassword = (token, password) =>
  axios({
    method: 'post',
    url: `${baseUrl}/api/v3/admin/resetpassword/${token}`,
    data: { password },
  });

// 取得 account 資料
export const getProfile = () =>
  request({
    method: 'get',
    url: '/api/v3/admin',
  });

// 更新 account 資料
export const updateProfile = (name, company, cellPhone, taxIDNumber, language, industry) =>
  request({
    method: 'post',
    url: '/api/v3/admin',
    data: {
      name,
      company,
      cellPhone,
      taxIDNumber,
      language,
      industry,
    },
  });

// 是否完成第一次使用
export const getIsDoneFirstTime = () =>
  request({
    method: 'get',
    url: '/api/v3/devices/hasLogin',
  });

// -------------- Event 相關--------------------
export const createEvent = (data) => {
  const { timeZone } = data;
  let defaultTimeZone = '';

  if (!timeZone) {
    defaultTimeZone = DEFAULT_EVENT_TIME_ZONE.value;
  } else {
    defaultTimeZone = timeZone;
  }

  return request({
    method: 'post',
    url: `/api/v6/project/create`,
    data: {
      ...data,
      timeZone: defaultTimeZone,
    },
  });
};

// 取得活動列表
export const getEventList = () =>
  request({
    method: 'get',
    url: `/api/v6/project/get`,
  });

// 取得活動
export const getEvent = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/project/get/${projectCode}`,
  });

// 刪除活動
export const deleteEvent = (projectCode) =>
  request({
    method: 'delete',
    url: `/api/v6/project/delete/${projectCode}`,
  });

// 更新活動
export const updateEvent = (projectCode, data) => {
  const { timeZone } = data;
  let defaultTimeZone = '';

  if (!timeZone) {
    defaultTimeZone = DEFAULT_EVENT_TIME_ZONE;
  } else {
    defaultTimeZone = timeZone;
  }

  return request({
    method: 'put',
    url: `/api/v6/project/update/${projectCode}`,
    data: {
      ...data,
      timeZone: defaultTimeZone,
    },
  });
};

/**
 * 更新活動的發布狀態與報名功能狀態
 *
 * @param {string} projectCode
 * @param {object} data: { isActive: Boolean, isSignUpEnabled: Boolean} 每次 PATCH，每個 properties 都要傳，若不傳會是 false
 * @returns
 */
export const patchEvent = (projectCode, data) => {
  return request({
    method: 'patch',
    url: `/api/v6/project/patch/${projectCode}`,
    data,
  });
};

// 活動操作記錄
export const getEventLog = (projectCode, skip = 0, length = 100) =>
  request({
    method: 'get',
    url: `/api/v6/project/${projectCode}/modulelogs?skip=${skip}&length=${length}`,
  });

// 所有活動操作記錄
export const getEventsLogs = (skip = 0, length = 100) =>
  request({
    method: 'get',
    url: `/api/v6/project/modulelogs?skip=${skip}&length=${length}`,
  });

// ----------- device code-----------
// 取得活動啟動碼
export const getDeviceCodeList = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/device`,
  });

// logout startup code
export const getDisuseDeviceCode = (projectCode, deviceSeq) =>
  request({
    method: 'delete',
    url: `/api/v6/${projectCode}/device/${deviceSeq}/disuse`,
  });

// create a new device code
export const postCreateDeviceCode = (projectCode) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/device`,
  });

// delete a device code
export const deleteDeviceCode = (projectCode, deviceSeq) =>
  request({
    method: 'delete',
    url: `/api/v6/${projectCode}/device/${deviceSeq}`,
  });

// ----------- booth device code-----------
// 取得攤位啟動碼
export const getBoothDeviceCodeList = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/boothdevice`,
  });

// logout booth device code
export const getDisuseBoothDeviceCode = (projectCode, deviceSeq) =>
  request({
    method: 'delete',
    url: `/api/v6/${projectCode}/device/${deviceSeq}/disuse`,
  });

// create a new booth device code
export const postCreateBoothDeviceCode = (projectCode) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/device`,
  });

// delete a booth device code
export const deleteBoothDeviceCode = (projectCode, deviceSeq) =>
  request({
    method: 'delete',
    url: `/api/v6/${projectCode}/device/${deviceSeq}`,
  });

// rename a booth device code
export const renameDeviceCode = (projectCode, deviceSeq, name) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/device/${deviceSeq}/rename?name=${name}`,
  });

// ------- 名單相關 ---------
// get participants with pagination、sort、fliter and search
/**
 *
 * @param { includeGlobal: [] } 全域搜尋，交集
 * @param { includeLocal: {} } 特定參數搜尋，交集
 * @returns
 */
export const getParticipants = ({
  projectCode,
  isAllticket = false,
  page = 1,
  size = 20,
  sort = '0',
  isAscending = true,
  includeGlobal = [],
  includeLocal = {},
}) => {
  function booleanParseToString(boolean) {
    return boolean ? 'True' : 'False';
  }

  const invalid =
    R.type(includeLocal.invalid) === 'Boolean' ? booleanParseToString(includeLocal.invalid) : '';
  const valid =
    R.type(includeLocal.valid) === 'Boolean' ? booleanParseToString(includeLocal.valid) : '';
  // 14，15 是給簡訊的(目前介面還沒有需要呈現所以沒用到)
  const local = {
    0: (includeLocal.userId ?? '').toString(),
    1: (includeLocal.name ?? '').toString(),
    2: (includeLocal.ticketUuid ?? '').toString(),
    3: (includeLocal.ticketName ?? '').toString(),
    4: (includeLocal.price ?? '').toString(),
    5: (includeLocal.reviewStatus ?? '').toString(),
    6: (includeLocal.paymentStatus ?? '').toString(),
    7: invalid ?? '',
    8: valid ?? '',
    9: (includeLocal.source ?? '').toString(),
    10: includeLocal.reviewPeriod ?? [],
    11: includeLocal.paymentPeriod ?? [],
    12: includeLocal.includeEmailSeq ?? [],
    13: includeLocal.excludeEmailSeq ?? [],
    16: includeLocal.includeSessionUuid ?? [],
    17: includeLocal.excludeSessionUuid ?? [],
  };

  return request({
    method: 'get',
    url: `/api/v6/${projectCode}/participant/get?option=${window.btoa(
      encodeURIComponent(
        JSON.stringify({
          page,
          size,
          sort,
          isAscending,
          global: includeGlobal,
          local,
          isAllticket,
        }),
      ),
    )}`,
  });
};

export const getParticipant = (projectCode, uuid) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/participant/get/${uuid}`,
  });

// get participant template
export const getParticipantTemplate = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6.2/${projectCode}/participant/template`,
    responseType: 'blob',
  });

// get participant luckydraw template
export const getParticipantLuckyDrawTemplate = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6.2/${projectCode}/participant/luckydraw/template`,
    responseType: 'blob',
  });

// import check
export const postImportCheck = (projectId, data) =>
  request({
    method: 'post',
    url: `/api/v4/${projectId}/Users/importCheck`,
    data,
  });

// v6 import list, 可選擇是否要更新或刪除現有名單資料(與匯入名單比對)
export const postImportUserListWithCustomMode = (
  projectCode,
  data,
  canDelete = true,
  canUpdate = true,
  isCheck = false,
) =>
  request({
    method: 'post',
    url: `/api/v6.2/${projectCode}/participant/import`,
    params: {
      isDeleteEnabled: canDelete,
      isUpdateEnabled: canUpdate,
      isCheck,
    },
    data,
  });

// 取得來賓欄位
export const getParticipantColumns = (projectCode) =>
  request({
    methods: 'get',
    url: `/api/v6/${projectCode}/participant/column/get`,
  });

// v6 更新名單欄位(也是報名表單欄位)
export const putUserListColumns = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/participant/column/modify`,
    data,
  });

// v6 僅更新clientAppSortId欄位(也是報名表單欄位)
export const patchUserListColumns = (projectCode, data) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/participant/column/modify`,
    data,
  });

// create participants
export const postParticipants = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6.2/${projectCode}/participant/create`,
    data,
  });

// v6 update a user in list
export const updateParticipant = (projectCode, uuid, data) =>
  request({
    method: 'put',
    url: `/api/v6.2/${projectCode}/participant/update/${uuid}`,
    data,
  });

// delete a user in list
export const deleteSingleRowInUserList = (proejctCode, uuid) =>
  request({
    method: 'delete',
    url: `/api/v6.1/${proejctCode}/participant/delete/${uuid}`,
  });

// check userId 有無重複 in list
export const checkParticipantIdDuplicated = (projectCode, update, current = undefined) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/participant/check`,
    params: {
      update,
      current,
    },
  });

// validate participant data pattern
/**
 *
 * @param {string} projectCode
 * @param {number} receiverType 0: email, 1: phone
 * @returns
 */
export const validateParticipantDataPattern = (projectCode, receiverType) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/participant/validatepattern?receiverType=${receiverType}`,
  });

// export user list
// export const getExportParticipantList = (projectCode) =>
//   request({
//     method: 'get',
//     url: `/api/v6/${projectCode}/participant/export`,
//     responseType: 'blob',
//   });

export const getExportFilteredParticipants = (projectCode, localFilter, keywords) => {
  function booleanParseToString(boolean) {
    return boolean ? 'True' : 'False';
  }

  const invalid =
    R.type(localFilter.invalid) === 'Boolean' ? booleanParseToString(localFilter.invalid) : '';
  const valid =
    R.type(localFilter.valid) === 'Boolean' ? booleanParseToString(localFilter.valid) : '';
  const local = {
    0: (localFilter.userId ?? '').toString(),
    1: (localFilter.name ?? '').toString(),
    2: (localFilter.ticketUuid ?? '').toString(),
    3: (localFilter.ticketName ?? '').toString(),
    4: (localFilter.price ?? '').toString(),
    5: (localFilter.reviewStatus ?? '').toString(),
    6: (localFilter.paymentStatus ?? '').toString(),
    7: invalid ?? '',
    8: valid ?? '',
    9: (localFilter.source ?? '').toString(),
    10: localFilter.reviewPeriod ?? [],
    11: localFilter.paymentPeriod ?? [],
    12: localFilter.includeEmailSeq ?? [],
    13: localFilter.excludeEmailSeq ?? [],
    16: localFilter.includeSessionUuid ?? [],
    17: localFilter.excludeSessionUuid ?? [],
  };

  return request({
    method: 'get',
    url: `/api/v6.3/${projectCode}/participant/export?option=${window.btoa(
      encodeURIComponent(
        JSON.stringify({
          global: keywords,
          local,
        }),
      ),
    )}`,
    responseType: 'blob',
  });
};

// prepare user check-in QR code image
export const postPrepareCheckinQrImage = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/qrimage/prepare/ticketuuid`,
    data,
  });

// prepare user index-page QR code image
export const postPrepareIndexPageQrImage = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/qrimage/prepare/indexpageurl`,
    data,
  });

// 取得下載 checkin QR 圖檔紀錄
export const getCheckinQRImageDownloadLogs = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/qrimage/get`,
  });

// 更新 QR 圖檔準備紀錄最後下載時間
export const putPrepareQRImageLastDownload = (projectCode, seq) =>
  request({
    method: 'put',
    url: `/api/v6/${projectCode}/qrimage/${seq}/update`,
  });

// ------- email template 相關 -------
// GET email templates
export const getEmailTemplates = () =>
  request({
    method: 'get',
    url: `/api/v6/emailtemplate/get`,
  });

// POST email templates
export const postEmailTemplate = (name, html, json, emailTitle = '') => {
  return request({
    method: 'post',
    url: `/api/v6/emailtemplate/create`,
    data: { name, html, json, emailTitle },
  });
};

// DELETE email templates
export const deleteEmailTemplate = (seq) =>
  request({
    method: 'delete',
    url: `/api/v6/emailtemplate/delete/${seq}`,
  });

// PATCH email templates
export const patchEmailTemplate = (templateId, name, html, json, emailTitle) =>
  request({
    method: 'put',
    url: `/api/v6/emailtemplate/update/${templateId}`,
    data: { name, html, json, emailTitle },
  });

// 取得電子郵件樣板設定 (自動寄信)
export const getEmailTemplateSetting = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/emailtemplate/${projectCode}/setting/get`,
  });
// 更新電子郵件樣板設定 (自動寄信)
export const updateEmailTemplateSetting = (projectCode, data) =>
  request({
    method: 'put',
    url: `/api/v6/emailtemplate/${projectCode}/setting/update`,
    data,
  });

// ---- email 伺服器設定相關 -----
// GET 郵件伺服器設定
export const getMailServerSetting = (projectCode) =>
  request({
    method: 'get',
    url: `api/v6/${projectCode}/emailserver/get`,
  });

// 建立 郵件伺服器設定
export const postMailServerSetting = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/emailserver/create`,
    data,
  });

// 連線測試 郵件伺服器設定
export const testMailServerSetting = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/emailserver/test`,
    data,
  });

// 更新 郵件伺服器設定
export const putMailServerSetting = (projectCode, data) =>
  request({
    method: 'put',
    url: `/api/v6/${projectCode}/emailserver/update`,
    data,
  });

// 僅更新開啟關閉 郵件伺服器設定
export const patchMailServerSetting = (projectCode, data) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/emailserver/patch`,
    data,
  });
// ---- email 草稿與信件列表相關 -----
// GET email 信件列表
export const getEmailList = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/emaildraft/get`,
  });

// POST email 草稿
export const postEmailList = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/emaildraft/create`,
    data: { ...data },
  });

// GET email 草稿
export const getEmailDraft = (projectCode, draftSeq) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/emaildraft/${draftSeq}/get`,
  });

export const deleteEmailDraft = (projectCode, draftSeq) =>
  request({
    method: 'delete',
    url: `/api/v6/${projectCode}/emaildraft/${draftSeq}/delete`,
  });

export const patchEmailDraft = (porjectCode, draftSeq, data) =>
  request({
    method: 'put',
    url: `/api/v6/${porjectCode}/emaildraft/${draftSeq}/update`,
    data,
  });

// copy email 草稿
export const postCopyEmailList = (projectCode, draftSeq) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/emaildraft/${draftSeq}/clone`,
  });

// 單筆 email 瀏覽內文
export const getEmailPreview = (projectCode, draftSeq) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/emaildraft/${draftSeq}/preview`,
  });

// ------ 信件發送記錄 ------
export const getSingleEmailRecord = (projectCode, draftSeq) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/emaildraft/${draftSeq}/receivers`,
  });

export const getExportedSingleEmailRecord = (projectCode, draftSeq) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/emaildraft/${draftSeq}/receivers/export`,
    responseType: 'blob',
  });

// ----------- SMS ----------------
export const getSmsList = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/smsdraft/get`,
  });

export const getSmsDraft = (projectCode, draftSeq = '') =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/smsdraft/${draftSeq}/get`,
  });

export const postSmsDraft = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/smsdraft/create`,
    data,
  });

export const patchSmsDraft = (projectCode, draftSeq, data) =>
  request({
    method: 'put',
    url: `/api/v6/${projectCode}/smsdraft/${draftSeq}/update`,
    data,
  });

export const deleteSmsDraft = (projectCode, draftSeq) =>
  request({
    method: 'delete',
    url: `/api/v6/${projectCode}/smsdraft/${draftSeq}/delete`,
  });

export const postCopySmsDraft = (projectCode, draftSeq) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/smsdraft/${draftSeq}/clone`,
  });

export const getSingleSmsRecord = (projectCode, draftSeq) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/smsdraft/${draftSeq}/receivers`,
  });

export const getSmsPreview = (projectCode, draftSeq) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/smsdraft/${draftSeq}/preview`,
  });

export const postContentToGetRealCost = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/point/estimatecost`,
    data,
  });

// ------ POINT --------------
// get 點數
export const getPoint = () =>
  request({
    method: 'get',
    url: '/api/v6/point',
  });

// get 扣點記錄列表
export const getPointLog = () =>
  request({
    method: 'get',
    url: `/api/v6/point/orders`,
  });

// get 單筆扣點詳細記錄
export const getDetailPointLog = (seq) =>
  request({
    method: 'get',
    url: `/api/v6/point/order/${seq}/logs`,
  });

// 匯出點數記錄
export const getExportPointLogs = () =>
  request({
    method: 'get',
    url: `/api/v6/point/export`,
    responseType: 'blob',
  });

// 取得 Event 特定參加者的人數，可以用於檢查 Email 草稿是否有勾選到特定參加者，在匯入新的名單時可以去判定要不要跳出警示
export const getParticularAttendeeNumber = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/participant/receivercount`,
  });

// --------------- File --------------
export const postFileUpload = (File) =>
  request({
    method: 'post',
    url: `/api/v5/file/upload`,
    data: File,
  });

// ---------------- Survey Form -----------
export const getSurveyFormList = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/surveyform`,
  });

export const postSurveyForm = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/surveyform`,
    data,
  });

export const getSurveyForm = (projectCode, uuid) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/surveyform/${uuid}`,
  });

export const putSurveyForm = (projectCode, uuid, data) =>
  request({
    method: 'put',
    url: `/api/v6/${projectCode}/surveyform/${uuid}`,
    data,
  });

export const patchSurveyFormBeginAt = (projectCode, uuid, value) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/surveyform/${uuid}/beginat${value ? `?v=${value}` : ''}`,
  });

export const patchSurveyFormEndAt = (projectCode, uuid, value) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/surveyform/${uuid}/endat${value ? `?v=${value}` : ''}`,
  });

export const patchSurveyFormActive = (projectCode, uuid, value) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/surveyform/${uuid}/isactive?v=${value}`,
  });

export const patchSurveyFormMustCheck = (projectCode, uuid, value) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/surveyform/${uuid}/mustCheck?v=${value}`,
  });

export const patchSurveyFormPrizeEnabled = (projectCode, uuid, value) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/surveyform/${uuid}/isprizeenabled?v=${value}`,
  });

export const deleteSurveyForm = (projectCode, uuid) =>
  request({
    method: 'delete',
    url: `/api/v6/${projectCode}/surveyform/${uuid}`,
  });

export const getGuestSurveyForm = (projectCode, uuid, userCode = '', id = '') =>
  request({
    method: 'get',
    url: `/api/v6.1/${projectCode}/surveyformguest/${uuid}?userCode=${userCode}&id=${id}`,
  });

// 來賓送出問卷表單
export const postGuestSurveyForm = (projectCode, uuid, version, userCode, id = '', data) =>
  request({
    method: 'post',
    url: `/api/v6.1/${projectCode}/surveyformguest/${uuid}/${version}?userCode=${userCode}&id=${id}`,
    data,
  });

// 來賓領取問卷禮
export const postGuestObtainSurveyFormGift = (projectCode, uuid, token) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/surveyformguest/${uuid}?token=${token}`,
  });

// 使用舊問卷網址(v1.8 以前只有 uuid)去取得 projectCode 導向到新問卷網址(v1.8 以後)
export const getSurveyFormProjectCodeFromUuid = (surveyFormUuid) =>
  request({
    method: 'get',
    url: `/api/v6/surveyformguest/${surveyFormUuid}`,
  });

export const getPreviewGuestSurveyForm = (uuid) =>
  request({
    method: 'get',
    url: `/api/v5/surveyForm/${uuid}/Preview`,
  });

export const getExportSurveyFormResult = (projectCode, uuid) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/surveyform/${uuid}/export`,
    responseType: 'blob',
  });

// --------------- helath form ----------------
export const getHealthFormSetting = (projectCode) =>
  request({
    methodl: 'get',
    url: `/api/v3/${projectCode}/form/setting`,
  });

export const patchHealthFormSetting = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v3/${projectCode}/form/setting`,
    data,
  });

export const getHealthFormTemplates = () =>
  request({
    method: 'get',
    url: `/api/v3/form/template`,
  });

// ------------- index page -------------
// GET index page setting
export const getIndexPageEdit = (projectCode, lang) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/indexPage/get?lang=${lang}`,
  });

// GET index page preview
export const getIndexPagePreview = (projectCode, uuid) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/indexpage/preview?uuid=${uuid}`,
  });

// post index page
export const postIndexPage = (projectCode, data) => {
  return request({
    method: 'post',
    url: `/api/v6/${projectCode}/indexpage/create`,
    data,
  });
};

// put index page setting
export const patchIndexPageEdit = (projectCode, data) => {
  return request({
    method: 'put',
    url: `/api/v6/${projectCode}/indexPage/update`,
    data,
  });
};

// 來賓取得 index page app setting
export const getGuestIndexPage = (projectCode, userCode) =>
  request({
    method: 'get',
    url: userCode
      ? `/api/v6/${projectCode}/guestindexpage/get?userCode=${userCode}`
      : `/api/v6/${projectCode}/guestindexpage/get`,
  });

// --------------- badge ----------------
export const getBadges = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/badge/get`,
  });

export const getBadgeSetting = (projectCode, seq) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/badge/get/${seq}`,
  });

export const postBadgeSetting = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/badge/create`,
    data,
  });

export const postCopyBadge = (projectCode, seq) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/badge/clone/${seq}`,
  });

export const deleteBadgeSetting = (projectCode, seq) =>
  request({
    method: 'delete',
    url: `/api/v6/${projectCode}/badge/delete/${seq}`,
  });

export const putBadgeSetting = (projectCode, seq, data) =>
  request({
    method: 'put',
    url: `/api/v6/${projectCode}/badge/update/${seq}`,
    data,
  });

export const patchBadgeSetting = (projectCode, seq, data) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/badge/patch/${seq}`,
    headers: {
      'content-type': 'application/json',
    },
    data: `"${data}"`,
  });

// --------------- ticket type ----------------
// 建立票券種類
export const postTicketType = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/tickettype/create`,
    data,
  });

// 取得票券種類列表
export const getTicketTypes = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/tickettype/get`,
  });

// 取得單一票種
export const getTicketType = (projectCode, uuid) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/tickettype/${uuid}/get`,
  });

// 刪除票券種類
export const deleteTicketType = (projectCode, uuid) =>
  request({
    method: 'delete',
    url: `/api/v6/${projectCode}/tickettype/${uuid}/delete`,
  });

// 更新票券種類
export const putTicketType = (projectCode, uuid, data) =>
  request({
    method: 'put',
    url: `/api/v6/${projectCode}/tickettype/${uuid}/update`,
    data,
  });

// 更新票券種類販售狀態
export const patchTicketTypeSelling = (projectCode, uuid, isSelling) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/tickettype/${uuid}/patch`,
    headers: {
      'content-type': 'application/json',
    },
    data: isSelling,
  });

// 取得票種統計
export const getTIcketStatistics = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/ticket/statistics`,
  });

// -------------- Ticket -----------
// 取得票券資訊
export const getTicket = (projectCode, userId) =>
  request({
    method: 'get',
    url: `/api/v5/${projectCode}/ticket/${userId}`,
  });

// 更新票券審核狀態
export const patchTicketReviewStatus = (projectCode, uuid, status) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/ticket/review/${uuid}`,
    headers: {
      'content-type': 'application/json',
    },
    data: status,
  });

// 更新票券中單一場次的審核狀態
export const patchSessionInTicketReviewStatus = (projectCode, uuid, suuid, status) =>
  request({
    method: 'patch',
    url: `/api/v6/${projectCode}/ticket/review/${uuid}/${suuid}`,
    headers: {
      'content-type': 'application/json',
    },
    data: status,
  });

// -------------- Guest Ticket -----------
// 取得票匣資訊: 包含 project、tickets、ticket sessions、buyer
export const getGuestTicketFolder = (projectCode, orderNo) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/signup/orderinfo/${orderNo}`,
  });

// 取得票券資訊: 包含 project、ticket、ticket sessions
export const getGuestTicket = (projectCode, uuid) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/signup/ticketinfo/${uuid}`,
  });

// -------------- Session 場次 -----------
// 取得多筆 session
export const getSessions = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/session/get`,
  });

// 新增多筆 session
/**
 *
 * @param {string} projectCode
 * @param {array} data 
 * [
    {
      "sortId": 1,
      "name": "QR Code 技術展示",
      "description": "請務必攜帶手機",
      "place": "南港展覽館",
      "kind": 0,
      "isPrintEnabled": true,
      "beginAt": 1700000000,
      "endAt": 1750000000
    }
   ]
 * @returns
 */
export const postSessions = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6.1/${projectCode}/session/create`,
    data,
  });

// 更新 session
/**
 *
 * @param {string} uuid
 * @param {object} data
 * @returns
 */
export const putSession = (projectCode, uuid, data) =>
  request({
    method: 'put',
    url: `/api/v6.1/${projectCode}/session/${uuid}/update`,
    data,
  });

// 刪除 session
export const deleteSession = (projectCode, uuid) =>
  request({
    method: 'delete',
    url: `/api/v6/${projectCode}/session/${uuid}/delete`,
  });

// -------------- payment setting -----------
// GET 金流設定
export const getPaymentSettings = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/paymentsettings/get`,
  });

// 建立 金流設定
export const postPaymentSettings = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/paymentsettings/create`,
    data,
  });

// 更新 金流設定
export const putPaymentSettings = (projectCode, data) =>
  request({
    method: 'put',
    url: `/api/v6/${projectCode}/paymentsettings/update`,
    data,
  });

// -------------- Guest Sign Up -----------
// GET 報名首頁資訊
export const getGuestSignUp = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/signup/form`,
  });

// 來賓取得報名表單
export const getGuestSignUpForm = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/signup/columns`,
  });

// 來賓報名
/**
 *
 * @param {string} projectCode
 * @param {object} data { orderNo: num, values: {} }
 * @returns
 */
export const postGuestSignUp = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6.2/${projectCode}/signup`,
    data,
  });

/**
 *
 * @param {string} projectCode
 * @param {object} data { orderNo: num, values: {} }
 * @returns
 */
export const postGuestSignUpCheck = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6.2/${projectCode}/signup/check`,
    data,
  });

/**
 *
 * @param {string} projectCode
 * @param {string} orderNo 訂單編號
 * @returns
 */
export const getGuestSignUpResult = (projectCode, orderNo) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/signup/signupresult/${orderNo}`,
  });

// -------------- Cache -----------
/**
 *
 * @param {array} keys
 * @returns
 */
export const getServerCacheValues = (keys) =>
  request({
    method: 'get',
    url: `/api/v5/cache/getvalues`,
    params: {
      keys,
    },
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

// -------------- system announcement -----------
// get all system announcement
export const getSystemAnnouncements = () =>
  request({
    method: 'get',
    url: '/api/v6/sysannounce/get',
  });

/**
 * post read announcement
 *
 * API 可以傳 array 的 seq，但是這邊還沒實作處理 query 值成 array
 * @param {int} seq
 * @returns
 */
export const postSystemAnnouncementsRead = (seq) =>
  request({
    method: 'post',
    url: `/api/v6/sysannounce/read?seqs=${seq}`,
  });

// --------------- statistic ------------
// 取得參加者出席率
export const getAttendeeCheckinSituation = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/statistic/get`,
  });

// 匯出統計報表
export const postExportStatisticCheckinReport = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/statistic/export`,
    responseType: 'blob',
  });

// post a new customize chart to list
export const postSingleCustomizeChart = (tokenId, projectId, data) =>
  request({
    method: 'post',
    url: `${testBaseUrl}/chartList`,
    data,
  });

// get customize chart list
export const getCustomizeChartList = () =>
  request({
    method: 'get',
    url: `${testBaseUrl}/chartList`,
  });

// delete customize a chart from list
export const deleteSingleCustomizeChart = (tokenId, projectId, seq) =>
  request({
    method: 'delete',
    url: `${testBaseUrl}/chartList/${seq}`,
  });

// 家庭日相關
export const getFamilyDayForm = (projectCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/familydayformpage/get`,
  });
export const postFamilyForm = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/familydayformpage/create`,
    data,
  });
export const putFamilyForm = (projectCode, data) =>
  request({
    method: 'put',
    url: `/api/v6/${projectCode}/familydayformpage/update`,
    data,
  });
export const postValidateForm = (projectCode, data) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/familydayformpage/query`,
    data,
  });

// 剩餘可報到人數
export const getCheckInQuota = () =>
  request({
    method: 'get',
    url: '/api/v6/checkinquota',
  });

// 報到紀錄
export const getCheckInLogs = () =>
  request({
    method: 'get',
    url: '/api/v6/checkinquota/logs',
  });

// 單筆報到詳細紀錄
export const getCheckInLogDetail = (seq) =>
  request({
    method: 'get',
    url: `/api/v6/checkinquota/log/${seq}/detail`,
  });

// 攤位集點
export const getBoothEvent = (projectCode, uuid, userCode) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/BoothEvent/${uuid}${userCode && `?userCode=${userCode}`} `,
  });

// 攤位集點兌獎
export const postRedeemPrize = (projectCode, prizeUuid, prizeToken) =>
  request({
    method: 'post',
    url: `/api/v6/${projectCode}/prize/${prizeUuid}/obtain?token=${prizeToken}`,
  });

// 取得獎項(問卷禮)設定
export const getPrizeStyling = (projectCode, prizeUuid, prizeToken) =>
  request({
    method: 'get',
    url: `/api/v6/${projectCode}/prize/${prizeUuid}?token=${prizeToken}`,
  });

// -------------- Lucky Draw 抽獎相關 -----------
// 取得中獎名單
export const getWinnerList = (projectCode) =>
  onsiteRequest({
    method: 'get',
    url: `/api/v1/luckydraw/publish/get/${projectCode}`,
  });
